import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import Crumbs from "@/components/Crumbs";
import { saveCurrentScroll, useScrollRestore } from "@/lib/scrollRestore";
import { brl, brlShort, STAGES, MANDALA, stageLabel } from "@/lib/format";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Clock, ArrowRight, Search, X, ChevronDown, ChevronLeft, ChevronRight, AlertTriangle, AlertCircle } from "@/components/icons";
import { tokens } from "@/components/mpires";
import StickyHScrollbar from "@/components/StickyHScrollbar";
import HelpTip from "@/components/HelpTip";
import GanhoModal from "@/components/GanhoModal";
import CelebrationOverlay from "@/components/CelebrationOverlay";

const ACTIVE_STAGES = ["Prospecção", "Qualificação", "Testes", "Proposta", "Negociação", "Fechado", "Perdido"];
// Lote 4 itens 3+4: as 5 primeiras são "abertas" (ganham largura igual com
// `flex-1` no desktop); Fechado e Perdido continuam sendo áreas de soltar
// (nunca ocultas), em colunas mais estreitas no fim.
const OPEN_STAGES_KANBAN = ACTIVE_STAGES.slice(0, 5);
const CLOSED_STAGES_KANBAN = ACTIVE_STAGES.slice(5);
const alertDot = { red: "bg-bad", yellow: "bg-warn", green: "bg-ok", none: "bg-slate-300" };

// Lote 4 correção #1 — compara título e cliente sem espaços nas pontas e
// sem diferença de maiúsculas/minúsculas (ex.: "Palato Alimentações " ==
// "Palato Alimentações"; "Tribu Açai" == "Tribu açai").
function sameTituloCliente(titulo, cliente) {
  if (!titulo || !cliente) return false;
  const norm = (s) => String(s).trim().toLocaleLowerCase("pt-BR");
  return norm(titulo) === norm(cliente);
}

// Lote 4 item 4 — status da próxima atividade usando o campo `next_action`
// que a API já devolve. Comparação por data (YYYY-MM-DD) no fuso
// America/Sao_Paulo; cor vinda SEMPRE de tokens.js.
function proximaAcaoStatus(na) {
  if (!na?.data) {
    return { kind: "nenhuma", color: tokens.colors.neutro[600], Icon: AlertCircle, label: "Sem próxima ação", tip: "Sem próxima ação definida" };
  }
  const fmt = { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit" };
  const hoje = new Date().toLocaleDateString("en-CA", fmt);
  const data = new Date(na.data);
  const dia = isNaN(data) ? null : data.toLocaleDateString("en-CA", fmt);
  const dataLegivel = isNaN(data) ? "" : data.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const tipo = na.tipo || "Atividade";
  const tip = `${tipo}${dataLegivel ? ` · ${dataLegivel}` : ""}`;
  if (!dia || dia < hoje) return { kind: "atrasada", color: tokens.colors.vermelho, Icon: AlertTriangle, label: `Atrasada — ${tipo}`, tip };
  if (dia === hoje) return { kind: "hoje", color: tokens.colors.ambar, Icon: Clock, label: `Hoje — ${tipo}`, tip };
  return { kind: "futura", color: tokens.colors.neutro[600], Icon: Clock, label: `Futura — ${tipo}`, tip };
}

export default function Pipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Lote 4 item 4 — "Responsável" só para gestão (gerente, diretor,
  // diretor_geral e admin). Para vendedor, não mostra.
  const showResponsavel = ["manager", "director", "diretor_geral", "admin"].includes(user?.role);
  const [sp, setSp] = useSearchParams();
  const scopeId = sp.get("scope_id") || "";
  const apenasAtivas = sp.get("apenas_ativas") || "";
  const fechadoMes = sp.get("fechado_mes") || "";
  const perdidoMes = sp.get("perdido_mes") || "";
  // Propaga scope + filtros temporais do drill-down para o backend.
  const fetchQs = new URLSearchParams();
  if (scopeId) fetchQs.set("scope_id", scopeId);
  if (apenasAtivas) fetchQs.set("apenas_ativas", "true");
  if (fechadoMes) fetchQs.set("fechado_mes", fechadoMes);
  if (perdidoMes) fetchQs.set("perdido_mes", perdidoMes);
  const fetchUrl = fetchQs.toString() ? `/deals?${fetchQs.toString()}` : "/deals";
  const { data, loading, error, reload } = useFetch(fetchUrl);
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const f = {
    q: sp.get("q") || "",
    owner: sp.get("owner") || "",
    fase: sp.get("fase") || "",
    stage: sp.get("stage") || "",
  };
  const updF = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    setSp(next, { replace: true });
  };
  const [expanded, setExpanded] = useState({ "Prospecção": true });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Modo de visualização: cascata vertical (padrão no celular) ou kanban
  // horizontal (padrão no desktop ≥ 768 px — Lote 4 item 6). Persiste no
  // localStorage por usuário; em telas < lg força cascata.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("pipeline-view-mode");
      if (saved) return saved;
      // Sem preferência salva: kanban no desktop, cascata no celular.
      if (typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches) return "kanban";
      return "cascata";
    } catch { return "cascata"; }
  });
  const changeViewMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem("pipeline-view-mode", m); } catch { /* ignore */ }
  };

  // Referência ao container horizontal do funil (desktop). Usada para:
  // 1) Resetar ao carregar (sempre começa em Prospecção — nunca com coluna cortada).
  // 2) Setas ◀ ▶ para navegar por largura de coluna, sem depender só do scroll.
  // 3) Alternar a visibilidade das setas conforme a posição do scroll.
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);
  // Sempre inicia em Prospecção (scrollLeft=0) ao (re)renderizar com dados —
  // evita o cenário "pecção" cortada de que o usuário reclamou.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollLeft = 0;
    updateArrows();
  }, [loading, updateArrows]);
  const scrollByCol = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const first = el.querySelector("[data-col]");
    // 320px da coluna + 16px do gap (gap-4 do Tailwind)
    const step = first ? first.getBoundingClientRect().width + 16 : 336;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useScrollRestore(!loading && !error);
  const openDeal = (id) => { saveCurrentScroll(); navigate(`/deal/${id}`); };

  const move = async (deal, stage) => {
    if (stage === deal.stage) return;
    // LOTE G1 — arrastar/mover para Fechado abre o quadro "Fechar como GANHO".
    if (stage === "Fechado") {
      setGanhoDeal(deal);
      return;
    }
    let motivo_perda;
    if (stage === "Perdido") {
      motivo_perda = window.prompt("Motivo da perda:");
      if (!motivo_perda || !motivo_perda.trim()) return;
    }
    setBusy(true);
    try {
      await api.patch(`/deals/${deal.id}/stage`, { stage, motivo_perda });
      toast.success(`Movido para ${stage}`);
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.erro === "cnpj_obrigatorio") {
        toast.error("Informe o CNPJ do cliente para avançar.", {
          action: { label: "Editar cliente", onClick: () => navigate(`/cliente/${d.cliente_id}/editar`) },
        });
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setBusy(false);
    }
  };

  // LOTE G1 — estado do modal / overlay compartilhados pelo funil.
  const [ganhoDeal, setGanhoDeal] = useState(null);
  const [ganhoBusy, setGanhoBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const confirmarGanhoFunnel = async ({ valor_fechado, data_fechamento }) => {
    if (!ganhoDeal) return;
    setGanhoBusy(true);
    try {
      await api.patch(`/deals/${ganhoDeal.id}/stage`, { stage: "Fechado", valor_fechado, data_fechamento });
      const primeiroNome = String(user?.name || "").trim().split(/\s+/)[0];
      const info = { clienteNome: ganhoDeal.cliente_nome || ganhoDeal.titulo || "cliente", valorFechado: valor_fechado, primeiroNome };
      setGanhoDeal(null);
      setCelebrate(info);
      reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.erro === "cnpj_obrigatorio") {
        toast.error("Informe o CNPJ do cliente para avançar.", {
          action: { label: "Editar cliente", onClick: () => navigate(`/cliente/${d.cliente_id}/editar`) },
        });
      } else if (d?.erro === "ganho_muito_cedo") {
        toast.error(d.mensagem || "Marcar Ganho só a partir de Proposta ou Negociação.");
      } else if (d?.erro === "ganho_sem_valor") {
        toast.error(d.mensagem || "Informe o valor mensal (> 0) para marcar Ganho.");
      } else {
        toast.error(errMsg(err));
      }
    } finally {
      setGanhoBusy(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  // LOTE CG-1 G3 — Ganho/Perdido no funil mostram só o fechado no MÊS atual
  // (America/Sao_Paulo). A fonte da data é `stage_updated_at` (o backend
  // atualiza isso no PATCH /stage; `deal_stage_history` é o fallback).
  const _mesAtualSP = (() => {
    const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit" });
    return fmt.format(new Date()).slice(0, 7); // YYYY-MM
  })();
  const _emMesAtual = (d) => {
    const at = d.stage_updated_at || d.hist_last_at || d.created_at || "";
    return String(at).slice(0, 7) === _mesAtualSP;
  };

  const deals = data || [];
  const owners = [...new Set(deals.map((d) => d.owner_name).filter(Boolean))].sort();
  const filtered = deals.filter((d) => {
    if (f.q && !`${d.titulo} ${d.cliente_nome || ""}`.toLowerCase().includes(f.q.toLowerCase())) return false;
    if (f.owner && d.owner_name !== f.owner) return false;
    if (f.fase === "sem" && d.fase_mandala) return false;
    if (f.fase && f.fase !== "sem" && String(d.fase_mandala) !== f.fase) return false;
    if (f.stage && (d.stage || "Prospecção") !== f.stage) return false;
    // LOTE CG-1 G3 — Fechado/Perdido só do mês atual nas colunas do funil.
    if ((d.stage === "Fechado" || d.stage === "Perdido") && !_emMesAtual(d)) return false;
    return true;
  });
  // LOTE G1 — para a coluna "Fechado" soma-se o valor realmente fechado quando houver.
  const _valorEfetivo = (d) => Number((d.stage === "Fechado" && d.valor_fechado) ? d.valor_fechado : d.valor) || 0;
  const byStage = (s) => filtered.filter((d) => (d.stage || "Prospecção") === s);
  const columnTotal = (s) => byStage(s).reduce((sum, d) => sum + _valorEfetivo(d), 0);
  const columnWeighted = (s) => byStage(s).reduce((sum, d) => sum + (Number(d.valor) || 0) * (Number(d.prob) || 0) / 100, 0);
  // CRM-010 Lote 1: "Total em aberto" e "Previsão ponderada" somam só
  // oportunidades abertas (exclui Fechado/Perdido), como o subtítulo já diz.
  const abertas = filtered.filter((d) => d.stage !== "Fechado" && d.stage !== "Perdido");
  const totalValor = abertas.reduce((sum, d) => sum + (Number(d.valor) || 0), 0);
  const totalPonderado = abertas.reduce((sum, d) => sum + (Number(d.valor) || 0) * (Number(d.prob) || 0) / 100, 0);
  const activeFilters = f.q || f.owner || f.fase || f.stage;

  const onDrop = (stage) => {
    setOverStage(null);
    const deal = deals.find((d) => d.id === dragId);
    setDragId(null);
    if (deal) move(deal, stage);
  };

  return (
    <div>
      <PageHeader
        title="Funil de vendas"
        subtitle={(() => {
          const abertas = deals.filter((d) => d.stage !== "Fechado" && d.stage !== "Perdido").length;
          return `${abertas} oportunidade${abertas === 1 ? "" : "s"} aberta${abertas === 1 ? "" : "s"} no funil (exclui Fechado/Perdido)`;
        })()}
        action={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/pipeline/encerradas")} data-testid="pipe-encerradas-btn"
              className="shrink-0 flex items-center justify-center gap-2 h-10 px-3 rounded-lg border border-input bg-white text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Clock className="w-4 h-4" /><span className="hidden md:inline">Encerradas</span>
            </button>
            <button onClick={() => navigate("/deal/novo")} data-testid="new-deal-btn"
              aria-label="Nova oportunidade"
              className="shrink-0 flex items-center justify-center gap-2 h-11 w-11 md:h-10 md:w-auto md:px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors">
              <Plus className="w-4 h-4" /><span className="hidden md:inline">Nova oportunidade</span>
            </button>
          </div>
        }
      />

      {(() => {
        const activeCount = [f.q, f.owner, f.fase, f.stage].filter(Boolean).length;
        const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
        const shown = filtersOpen || isDesktop;
        return (
          <div className="mb-5">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="pipe-filters-toggle"
              className="lg:hidden w-full flex items-center justify-between h-10 px-3 rounded-lg border border-input bg-white text-sm font-medium text-navy"
            >
              <span className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                Filtros{activeCount > 0 ? ` (${activeCount})` : ""}
              </span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <div className={`${shown ? "flex" : "hidden"} lg:flex flex-wrap items-center gap-2 mt-3 lg:mt-0`} data-testid="pipe-filters-panel">
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={f.q} onChange={(e) => updF({ q: e.target.value })} placeholder="Filtrar por título/cliente..." data-testid="pipe-filter-q"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40" />
                </div>
                {owners.length > 1 && (
                  <select value={f.owner} onChange={(e) => updF({ owner: e.target.value })} data-testid="pipe-filter-owner"
                    className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
                    <option value="">Todos responsáveis</option>
                    {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                <select value={f.fase} onChange={(e) => updF({ fase: e.target.value })} data-testid="pipe-filter-fase"
                  className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
                  <option value="">Toda a metodologia Allia</option>
                  {[1, 2, 3, 4].map((n) => <option key={n} value={String(n)} label={`Allia ${n} · ${MANDALA[n]}`} />)}
                  <option value="sem">Sem classificação Allia</option>
                </select>
                <select value={f.stage} onChange={(e) => updF({ stage: e.target.value })} data-testid="pipe-filter-stage"
                  className="h-10 px-3 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40">
                  <option value="">Todas etapas</option>
                  {ACTIVE_STAGES.map((s) => <option key={s} value={s}>{stageLabel(s)}</option>)}
                </select>
                {activeFilters && (
                  <button onClick={() => updF({ q: "", owner: "", fase: "", stage: "" })} data-testid="pipe-filter-clear"
                    className="h-10 px-3 rounded-lg border border-input bg-white text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-1.5">
                    <X className="w-4 h-4" /> Limpar
                  </button>
                )}
                {activeFilters && <span className="text-xs text-muted-foreground">{filtered.length} de {deals.length}</span>}
              </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-5" data-testid="funil-resumo">
        <div className="rounded-xl border border-black/5 bg-card px-2.5 py-2.5 sm:px-4 sm:py-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)] min-w-0">
          <div className="text-[11px] sm:text-xs text-muted-foreground leading-tight">Total em aberto</div>
          <div className="text-sm sm:text-lg font-bold text-navy mt-0.5 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis leading-tight" title={brl(totalValor)} data-testid="funil-total-valor">
            <span className="sm:hidden">{brlShort(totalValor)}</span>
            <span className="hidden sm:inline">{brl(totalValor)}</span>
          </div>
        </div>
        <div className="rounded-xl border border-brand/20 bg-brand-soft/40 px-2.5 py-2.5 sm:px-4 sm:py-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)] min-w-0">
          <div className="text-[11px] sm:text-xs text-brand font-medium leading-tight">Previsão ponderada</div>
          <div className="text-sm sm:text-lg font-bold text-brand mt-0.5 tabular-nums whitespace-nowrap overflow-hidden text-ellipsis leading-tight" title={brl(totalPonderado)} data-testid="funil-total-ponderado">
            <span className="sm:hidden">{brlShort(totalPonderado)}</span>
            <span className="hidden sm:inline">{brl(totalPonderado)}</span>
          </div>
        </div>
        <div className="col-span-2 sm:col-span-1 rounded-xl border border-black/5 bg-card px-2.5 py-2.5 sm:px-4 sm:py-3 shadow-[0_1px_6px_rgb(0,0,0,0.04)] min-w-0">
          <div className="text-[11px] sm:text-xs text-muted-foreground leading-tight">Total no filtro <span className="text-[10px] text-slate-400">(exclui Fechado/Perdido)</span></div>
          <div className="text-sm sm:text-lg font-bold text-navy mt-0.5 tabular-nums whitespace-nowrap leading-tight" data-testid="funil-total-qtd">{abertas.length}</div>
        </div>
      </div>

      <div className="hidden lg:flex items-center justify-end gap-2 mb-3">
        <span className="text-xs text-muted-foreground">Visualização</span>
        <div role="tablist" className="inline-flex rounded-lg border border-black/10 bg-white p-0.5 shadow-[0_1px_4px_rgb(0,0,0,0.03)]" data-testid="pipeline-view-toggle">
          <button role="tab" aria-selected={viewMode === "cascata"} onClick={() => changeViewMode("cascata")} data-testid="pipeline-view-cascata"
            className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${viewMode === "cascata" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            Cascata
          </button>
          <button role="tab" aria-selected={viewMode === "kanban"} onClick={() => changeViewMode("kanban")} data-testid="pipeline-view-kanban"
            className={`h-8 px-3 rounded-md text-xs font-medium transition-colors ${viewMode === "kanban" ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            Kanban horizontal
          </button>
        </div>
      </div>

      <div className={`${viewMode === "kanban" ? "hidden lg:block" : "hidden"} rounded-2xl border border-black/5 bg-slate-50/60 p-4 relative`}>
        {/* Setas de navegação laterais — visíveis apenas quando há mais
            conteúdo do lado correspondente. Mantêm o padrão de acessibilidade
            (aria-label + botão de teclado) e não interferem no drag-and-drop. */}
        <button
          type="button"
          aria-label="Ver etapas anteriores"
          onClick={() => scrollByCol(-1)}
          disabled={!canPrev}
          data-testid="funil-nav-prev"
          className={`absolute left-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-black/10 shadow-md flex items-center justify-center text-slate-600 hover:text-navy hover:border-brand/40 transition-all ${canPrev ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Ver próximas etapas"
          onClick={() => scrollByCol(1)}
          disabled={!canNext}
          data-testid="funil-nav-next"
          className={`absolute right-1 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-black/10 shadow-md flex items-center justify-center text-slate-600 hover:text-navy hover:border-brand/40 transition-all ${canNext ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <div
          ref={scrollerRef}
          className="flex gap-2 overflow-x-auto pb-3 pipeline-scroll scroll-smooth px-3"
          data-testid="funil-scroller"
        >
          {ACTIVE_STAGES.map((stage) => {
            const items = byStage(stage);
            const isClosed = CLOSED_STAGES_KANBAN.includes(stage);
            // Lote 4 item 3 — colunas abertas em largura igual (flex-1 basis-0)
            // com um piso de 180 px; a partir de 1280 px (viewport), as 5
            // abertas cabem lado a lado. Fechado/Perdido ficam mais estreitas
            // no fim e continuam sendo áreas de soltar.
            const colClass = isClosed
              ? "w-[132px] shrink-0 flex flex-col"
              : "flex-1 basis-0 min-w-[150px] flex flex-col";
            return (
              <div key={stage} data-col className={colClass} data-testid={`column-${stage}`}
                onDragOver={(e) => { e.preventDefault(); if (overStage !== stage) setOverStage(stage); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOverStage(null); }}
                onDrop={() => onDrop(stage)}>
                <div className="mb-2 px-2.5 py-2 rounded-xl bg-white border border-black/5 shadow-[0_1px_4px_rgb(0,0,0,0.03)]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-[13px] text-navy truncate" style={{ color: tokens.colors.marinho }}>{stageLabel(stage)}</span>
                    <span className="text-[11px] bg-slate-100 text-slate-600 rounded-full px-1.5 py-0.5 shrink-0 tabular-nums">{items.length}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground tabular-nums truncate">{brl(columnTotal(stage))}</div>
                  {!isClosed && (
                    <div className="text-[10px] text-brand font-medium tabular-nums truncate" data-testid={`column-ponderado-${stage}`}>
                      Pond. {brl(columnWeighted(stage))}
                    </div>
                  )}
                </div>
                <div className={`flex-1 space-y-1.5 rounded-xl transition-colors min-h-[80px] p-1 ${overStage === stage ? "bg-brand-soft/60 ring-2 ring-brand/30 ring-dashed" : ""}`}>
                  {items.map((d) => {
                    const sameTitulo = sameTituloCliente(d.titulo, d.cliente_nome);
                    // Lote 4 correção #2 — oportunidade encerrada não tem
                    // próxima ação; não renderiza o ícone em Fechado/Perdido.
                    const encerrada = d.stage === "Fechado" || d.stage === "Perdido";
                    const na = encerrada ? null : proximaAcaoStatus(d.next_action);
                    const NaIcon = na?.Icon;
                    return (
                      <div key={d.id} draggable
                        onDragStart={(e) => { setDragId(d.id); e.dataTransfer.effectAllowed = "move"; }}
                        onDragEnd={() => { setDragId(null); setOverStage(null); }}
                        className={`bg-card rounded-md border border-black/5 p-2 shadow-[0_1px_3px_rgb(0,0,0,0.03)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] hover:border-brand/20 transition-all group cursor-grab active:cursor-grabbing ${dragId === d.id ? "opacity-40" : ""}`}
                        data-testid={`deal-card-${d.id}`}>
                        <div className="flex items-start gap-1">
                          <button onClick={() => openDeal(d.id)}
                            className="flex-1 min-w-0 text-left"
                            style={{ fontFamily: tokens.typography.familia }}>
                            {/* 1. Título */}
                            <div className="text-[12px] font-semibold leading-tight line-clamp-2" style={{ color: tokens.colors.marinho }}>{d.titulo}</div>
                            {/* 2. Cliente — só se diferente do título */}
                            {!sameTitulo && d.cliente_nome ? (
                              <div className="text-[10px] text-muted-foreground truncate mt-0.5">{d.cliente_nome}</div>
                            ) : null}
                            {/* 3. Valor */}
                            <div className="text-[11px] font-bold mt-0.5 tabular-nums" style={{ color: tokens.colors.marinho }}>{brl(d.stage === "Fechado" && d.valor_fechado ? d.valor_fechado : d.valor)}</div>
                            {/* 4. Responsável — só gestão (gerente/diretor/diretor_geral/admin) */}
                            {showResponsavel && d.owner_name ? (
                              <div className="text-[10px] truncate" style={{ color: tokens.colors.neutro[600] }}>{d.owner_name}</div>
                            ) : null}
                          </button>
                          <div className="flex flex-col items-center gap-0.5 shrink-0">
                            {/* Lote 4 item 7 — menu ⋯ 32×32 com contraste e aria-label. */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button disabled={busy} aria-label="Mais ações"
                                  className="h-8 w-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-navy"
                                  data-testid={`deal-menu-${d.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/deal/${d.id}`)}>Abrir</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {STAGES.filter((s) => s !== d.stage).map((s) => (
                                  <DropdownMenuItem key={s} onClick={() => move(d, s)} data-testid={`move-${d.id}-${s}`}>
                                    <ArrowRight className="w-3.5 h-3.5 mr-2 text-slate-400" /> {s}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {/* 5. Ícone de status da próxima atividade. Cor
                                vem de tokens.js; dica ao passar o mouse e
                                rótulo acessível. Em oportunidade encerrada
                                (Fechado/Perdido), não mostra (Lote 4 correção
                                #2). */}
                            {na && (
                              <span
                                role="img"
                                aria-label={na.label}
                                title={na.tip}
                                data-testid={`deal-${d.id}-next-${na.kind}`}
                                className="inline-flex"
                              >
                                <NaIcon className="w-4 h-4" style={{ color: na.color }} />
                              </span>
                            )}
                          </div>
                        </div>
                        {/* 6. Dias na etapa — texto pequeno. Ocultado em
                            Fechado/Perdido (LOTE CG-1 G3). */}
                        {!encerrada && (
                          <HelpTip label={`${d.days_in_stage} dias nesta etapa${d.sla_days ? ` · limite de ${d.sla_days} dias` : ""} · ${d.alert_level === "red" ? "Atrasada" : d.alert_level === "yellow" ? "Atenção" : d.alert_level === "green" ? "No prazo" : "Sem SLA"}`}>
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground" data-testid={`deal-dias-${d.id}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${alertDot[d.alert_level] || "bg-slate-300"}`} />
                            <Clock className="w-3 h-3 shrink-0" /> {d.days_in_stage}d
                          </div>
                          </HelpTip>
                        )}
                      </div>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="flex items-center justify-center py-3" data-testid={`column-empty-${stage}`}>
                      {/* Lote 4 item 5 — sem botões "Nova oportunidade" dentro das colunas. */}
                      <div className="text-[10px] text-slate-400 text-center">Nenhuma</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Barra de rolagem horizontal fixa na base — só aparece se o Kanban
          estiver visível na tela e a barra nativa estiver abaixo da viewport. */}
      {viewMode === "kanban" && <StickyHScrollbar targetRef={scrollerRef} />}

      {/* Cascata vertical — padrão em todas as telas quando viewMode="cascata". Em telas < lg força cascata. */}
      <div className={`${viewMode === "cascata" ? "block" : "lg:hidden"} space-y-3`} data-testid="pipeline-mobile">
        {ACTIVE_STAGES.map((stage) => {
          const items = byStage(stage);
          const isOpen = !!expanded[stage];
          return (
            <div key={stage} className="bg-card rounded-2xl border border-black/5 overflow-hidden shadow-[0_2px_10px_rgb(0,0,0,0.03)]">
              <button onClick={() => setExpanded((s) => ({ ...s, [stage]: !s[stage] }))} data-testid={`stage-row-${stage}`}
                className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-3.5 active:bg-slate-50">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                  <span className="font-semibold text-[13px] sm:text-sm text-navy break-words min-w-0 flex-1 text-left leading-tight">{stageLabel(stage)}</span>
                  <span className="text-[11px] sm:text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5 shrink-0 tabular-nums">{items.length}</span>
                </div>
                <div className="text-right shrink-0 max-w-[42%]">
                  <div className="text-[13px] sm:text-sm font-semibold text-navy tabular-nums break-all leading-tight">{brl(columnTotal(stage))}</div>
                  {stage !== "Fechado" && stage !== "Perdido" && (
                    <div className="text-[10px] sm:text-[11px] text-brand font-medium tabular-nums break-all leading-tight">Ponderada {brl(columnWeighted(stage))}</div>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  {items.length === 0 && (
                    <div className="flex items-center justify-center py-4" data-testid={`stage-empty-${stage}`}>
                      <div className="text-xs text-slate-400">Nenhuma oportunidade</div>
                    </div>
                  )}
                  {items.map((d) => {
                    const mSameTitulo = sameTituloCliente(d.titulo, d.cliente_nome);
                    const mEncerrada = d.stage === "Fechado" || d.stage === "Perdido";
                    const mNa = mEncerrada ? null : proximaAcaoStatus(d.next_action);
                    const MNaIcon = mNa?.Icon;
                    return (
                    <div key={d.id} data-testid={`mdeal-${d.id}`}
                      className="w-full bg-slate-50 rounded-xl p-3.5 active:bg-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => openDeal(d.id)} className="flex-1 min-w-0 text-left">
                          <div className="font-medium text-sm leading-tight line-clamp-2" style={{ color: tokens.colors.marinho }}>{d.titulo}</div>
                          {/* Lote 4 correção #1 — cliente só se diferente do título (trim + sem caixa). */}
                          {!mSameTitulo && d.cliente_nome ? (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">{d.cliente_nome}</div>
                          ) : null}
                        </button>
                        <div className="flex flex-col items-center gap-0.5 shrink-0">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button disabled={busy} aria-label="Mais ações" className="h-8 w-8 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 hover:text-navy" data-testid={`mdeal-menu-${d.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/deal/${d.id}`)}>Abrir</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {STAGES.filter((s) => s !== d.stage).map((s) => (
                                <DropdownMenuItem key={s} onClick={() => move(d, s)} data-testid={`mmove-${d.id}-${s}`}>
                                  <ArrowRight className="w-3.5 h-3.5 mr-2 text-slate-400" /> {s}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {/* Lote 4 correção #3 — ícone de status da próxima atividade,
                              igual ao Kanban. Fechado/Perdido não exibe. */}
                          {mNa && (
                            <span
                              role="img"
                              aria-label={mNa.label}
                              title={mNa.tip}
                              data-testid={`mdeal-${d.id}-next-${mNa.kind}`}
                              className="inline-flex"
                            >
                              <MNaIcon className="w-4 h-4" style={{ color: mNa.color }} />
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Lote 4 correção #3 — valor sem chip de probabilidade. */}
                      <div className="mt-2 pt-2 border-t border-slate-200/70">
                        <span className="text-sm font-semibold" style={{ color: tokens.colors.marinho }}>{brl(d.stage === "Fechado" && d.valor_fechado ? d.valor_fechado : d.valor)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        {!mEncerrada && <>
                          <span className={`w-1.5 h-1.5 rounded-full ${alertDot[d.alert_level] || "bg-slate-300"}`} />
                          <Clock className="w-3 h-3 shrink-0" />
                          <span className="truncate">{d.days_in_stage}d nesta etapa{d.next_action ? ` · Próxima: ${d.next_action.tipo}` : ""}</span>
                        </>}
                        {mEncerrada && (
                          <span className="truncate">{d.stage === "Fechado" ? "Ganho" : "Perdido"} em {new Date(d.stage_updated_at || d.created_at).toLocaleDateString("pt-BR")}</span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* LOTE G1 — "Fechar como GANHO" acionado pelo drag/mover ou menu. */}
      <GanhoModal
        open={!!ganhoDeal}
        busy={ganhoBusy}
        clienteNome={ganhoDeal?.cliente_nome}
        valorPrevisto={ganhoDeal?.valor}
        onClose={() => !ganhoBusy && setGanhoDeal(null)}
        onConfirm={confirmarGanhoFunnel}
      />
      <CelebrationOverlay
        open={!!celebrate}
        clienteNome={celebrate?.clienteNome}
        valorFechado={celebrate?.valorFechado}
        primeiroNome={celebrate?.primeiroNome}
        onClose={() => setCelebrate(null)}
      />
    </div>
  );
}
